<template>
  <small-modal class="expanded-modal" @close="closeModal">
    <div class="main-wrapper">
      <div class="main-header">
        <h1 class="title">{{ $t("dashboard.topVideos.title") }}</h1>
        <button class="export-button" @click="handleExportData">
          {{ $t("dashboard.topVideos.export") }}<ExportIcon />
        </button>
      </div>
      <div class="header">
        <p class="subtitle">
          {{ $t("dashboard.topVideos.subtitle") }}
        </p>
        <p class="subtitle">
          {{ $t("dashboard.topVideos.numVideos") }}
          <span>{{ processedVideos.length }}</span>
        </p>
      </div>
      <div class="video-table-wrapper">
        <div class="video-table-header">
          <div
            class="header-item header-item-name"
            @click="handleHeaderClick('fileName')"
          >
            <p>{{ $t("dashboard.topVideos.videoName") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'fileName' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'fileName' && selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div class="header-item" @click="handleHeaderClick('viewTimeCount')">
            <p>{{ $t("dashboard.topVideos.totalView") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'viewTimeCount' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'viewTimeCount' && selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('avgViewDuration')"
          >
            <p>{{ $t("dashboard.topVideos.avgViewDuration") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'avgViewDuration' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'avgViewDuration' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('completionPercentage')"
          >
            <p>{{ $t("dashboard.topVideos.avgViewPercentage") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'completionPercentage' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'completionPercentage' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div class="header-item" @click="handleHeaderClick('videoShared')">
            <p>{{ $t("dashboard.topVideos.shared") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'videoShared' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'videoShared' && selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('conversionCount')"
          >
            <p>{{ $t("dashboard.topVideos.orders") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionCount' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionCount' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('conversionAmount')"
          >
            <p>{{ $t("dashboard.topVideos.revenue") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionAmount' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionAmount' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div class="header-item" @click="handleHeaderClick('conversionRate')">
            <p>{{ $t("dashboard.topVideos.conversion") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionRate' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionRate' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
        </div>
        <div class="video-table-body">
          <div
            v-for="(video, index) in processedVideos"
            :key="index"
            class="video-table-row"
          >
            <div class="table-cell cell-video-name">
              <div class="flex-cell">
                <div class="video-thumbnail-container">
                  <img
                    class="video-thumbnail"
                    :src="video.thumbnail"
                    alt="thumbnail"
                  />
                  <div
                    class="video-thumbnail-overlay"
                    @click="openVideoLibraryWithVideo(video.fileName)"
                  >
                    <span class="open-icon"><OpenIcon /></span>
                  </div>
                </div>
                <div class="name-date">
                  <p>
                    {{ truncateText(video.fileName, 20) }}
                  </p>
                  <span
                    >Imported:
                    {{ formatISODateToDDMMAA(video.creationDate) }}</span
                  >
                </div>
              </div>
            </div>
            <div class="table-cell">
              {{ video.viewTimeCount }}
            </div>
            <div class="table-cell">
              {{ formatDurationToMMSS(video.avgViewDuration) }} /
              {{ formatDurationToMMSS(video.videoDuration) }}
            </div>
            <div class="table-cell">
              {{ formatNumber(video.completionPercentage) }}%
            </div>
            <div class="table-cell">
              {{ video.videoShared }}
            </div>
            <div class="table-cell">
              {{ formatNumber(video.conversionCount) }}
            </div>
            <div class="table-cell">
              {{ currency }} {{ formatNumber(video.conversionAmount) }}
            </div>
            <div class="table-cell">
              {{ formatNumber(video.conversionRate) }}%
            </div>
          </div>
        </div>
      </div>
    </div>
  </small-modal>
</template>

<script>
import SmallModal from "@/components/shared/SmallModal.vue";
import SortArrow from "@/assets/svg/sort-arrow.svg?inline";
import ExportIcon from "@/assets/svg/export-icon.svg?inline";
import OpenIcon from "@/assets/svg/cta/open-icon.svg?inline";

export default {
  components: {
    SmallModal,
    SortArrow,
    ExportIcon,
    OpenIcon,
  },
  props: {
    videoMetrics: {
      type: Array,
      default: () => [],
    },
    fromDate: {
      type: Date,
      required: true,
    },
    toDate: {
      type: Date,
      required: true,
    },
    selectedCampaigns: {
      type: Array,
      default: () => [],
    },
    deviceSelectorPicked: {
      type: String,
      default: "allDevices",
    },
    isAllCampaignsSelected: {
      type: Boolean,
      default: false,
    },
    isAgency: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "$",
    },
  },
  data() {
    return {
      selectedSorting: "completionPercentage",
      selectedArrow: -1,
      processedVideos: [],
    };
  },
  mounted() {
    if (this.videoMetrics && Array.isArray(this.videoMetrics)) {
      this.processedVideos = this.videoMetrics.filter(
        (video) => video.isPremade !== true
      );
      this.applySort();
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    formatDurationToMMSS(totalSecondsWithDecimals) {
      if (
        totalSecondsWithDecimals === null ||
        totalSecondsWithDecimals === undefined ||
        isNaN(totalSecondsWithDecimals)
      ) {
        return "00:00";
      }
      const totalSeconds = Math.round(Number(totalSecondsWithDecimals));

      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      const paddedMinutes = String(minutes).padStart(2, "0");
      const paddedSeconds = String(seconds).padStart(2, "0");

      return `${paddedMinutes}:${paddedSeconds}`;
    },
    truncateText(text, maxLength) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + "...";
      }
      return text;
    },
    formatISODateToDDMMAA(isoDateString) {
      if (!isoDateString) {
        return "";
      }
      try {
        const dateObj = new Date(isoDateString);

        const day = String(dateObj.getDate()).padStart(2, "0");
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const year = String(dateObj.getFullYear()).slice(-2);

        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error(error);
      }
    },
    handleHeaderClick(sortKey) {
      if (this.selectedSorting === sortKey) {
        this.selectedArrow *= -1;
      } else {
        this.selectedSorting = sortKey;
        this.selectedArrow = 1;
      }
      this.applySort();
    },
    applySort() {
      if (!this.processedVideos) return;

      const sortKey = this.selectedSorting;
      const arrow = this.selectedArrow;

      this.processedVideos.sort((a, b) => {
        let valA = a[sortKey];
        let valB = b[sortKey];

        if (valA === undefined || valA === null)
          valA = arrow === 1 ? Infinity : -Infinity;
        if (valB === undefined || valB === null)
          valB = arrow === 1 ? Infinity : -Infinity;

        if (typeof valA === "string" && typeof valB === "string") {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        let comparison = 0;
        if (valA > valB) {
          comparison = 1;
        } else if (valA < valB) {
          comparison = -1;
        }
        return comparison * arrow;
      });
    },
    async handleExportData() {
      const exportParams = {
        fromDate: new Date(this.fromDate).toISOString(),
        toDate: new Date(this.toDate).toISOString(),
        siteId: this.$store.state.account.user.siteId,
        exportType: "videos",
      };

      if (!this.isAllCampaignsSelected && this.selectedCampaigns.length > 0) {
        exportParams.campaignId = this.selectedCampaigns.map((c) => c._id);
      }

      if (this.deviceSelectorPicked !== "allDevices") {
        exportParams.device = this.deviceSelectorPicked;
      }

      try {
        const result = await this.$store.dispatch(
          "dashboard/exportDashboardDataAction",
          exportParams
        );
        return result;
      } catch (error) {
        console.error(error);
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "0";
      }
      const num = Number(value);
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    openVideoLibraryWithVideo(fileName) {
      this.$emit("open-video-library-with-video", fileName);
      this.closeModal();
    },
  },
};
</script>

<style lang="scss">
.expanded-modal .small-modal.bigger {
  width: 90vw;
  max-width: 90vw;
  height: 90vh;
  max-height: 90vh;
  overflow: auto;
}
</style>

<style lang="scss" scoped>
.main-wrapper {
  padding: 32px;
}

.title {
  @include base-font;
  @include font-big;
  font-weight: 600;
}
.subtitle {
  @include base-font;
  @include font-normal;
  span {
    font-weight: 600;
  }
}

.videos-table-wrapper {
  border: 6px solid $dark-purple;
  border-radius: 8px;
}

.video-table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  background-color: #ececec;
}

.header-item {
  padding: 12px 16px;
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  p {
    margin: 0;
  }
}
.header-item-name {
  text-align: left;
  justify-content: flex-start;
}

.sorting-arrows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  transform: scale(60%);
}

.selected-arrow {
  path {
    fill: $light-purple;
  }
}

.reverse-arrow {
  transform: translateX(-0.9px);
  path {
    transform: rotate(180deg);
    transform-origin: 50% 50%;
  }
}

.video-table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  @include base-font;
  @include font-smaller;
  border-bottom: 1px solid $light-grey;

  &:hover {
    background-color: #f6f6f6;
  }
}

.table-cell {
  padding: 12px 16px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  &:last-child {
    border-right: none;
  }
}

.cell-video-name {
  text-align: left;
  justify-content: flex-start;
}

.video-thumbnail {
  width: 40px;
  border: 1px solid #757575;
  border-radius: 2px;
  display: block;
  box-sizing: border-box;
}

.video-thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  border-radius: 2px;
  cursor: pointer;
}

.video-thumbnail-container:hover .video-thumbnail-overlay {
  opacity: 1;
}

.open-icon {
  path {
    fill: white;
  }
}

.flex-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 71px;
}

.name-date {
  display: flex;
  flex-direction: column;
  gap: 8px;
  white-space: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  span {
    color: #7f7f7f;
  }
}
.main-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.export-button {
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  height: 30px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid $light-grey;
  color: $light-grey;
  background: white;
  border-radius: 3px;

  &:hover {
    border: 1px solid $dark-purple;
    background: $light-pink;
  }
}

.video-thumbnail-container {
  position: relative;
  display: inline-block;
  width: 40px;
  height: auto;
}
</style>
